// Notification-center state — the panel's open/tab state + Allen's notifications
// (the "From Allen" tab), shared by the bell badge, the panel, and Today's "See all".
//
// The center is an INBOX you clear: viewing an Allen finding (or clearing selected)
// removes it and the bell drops. Two data modes, one contract:
//   - DEMO: seedDemo() populates live + allen from static demo data (no backend).
//   - PROD: fetchAllen() reads GET /coach/notifications; view/clear hit the PATCH
//     endpoints. (Live in prod is owned by NotificationsStore, with its edge-aware
//     acknowledge; the nav sums both for the badge.)
//
// Distinct from Today's "Allen also flagged", which is the PERSISTENT view of the
// current findings (stays, read-only). Both refresh each nightly ETL.

import { defineStore } from 'pinia'
import axios from 'axios'
import { getAPIURL } from '@/config'
import { DEMO_ALLEN_NOTIFICATIONS } from '@/demo/allenNotificationsDemo'
import { loadDemoJSON, demoDaysAgo } from '@/api/demoData.js'

const API_URL = getAPIURL()

// CoachNotification.to_dict → the card shape the panel renders.
const mapCoachNotification = (n) => ({
  id: n.NotificationId,
  kind: n.Kind,
  severity: n.Severity,
  title: n.Title,
  detail: n.Body,
  deepLink: n.DeepLink,
  createdAt: n.CreatedAt,
})

export const useNotificationCenterStore = defineStore('notificationCenter', {
  state: () => ({
    open: false,
    activeTab: 'live',   // 'live' | 'allen'
    live: [],            // demo only (prod live lives in NotificationsStore)
    allen: [],           // [{id, kind, severity, title, detail, when?, deepLink, createdAt}]
    _demo: false,
  }),

  getters: {
    bellCount: (s) => s.live.length + s.allen.length,   // demo badge (prod nav sums both stores)
  },

  actions: {
    // ---------- DEMO ----------
    async seedDemo() {
      if (this._demo && (this.allen.length || this.live.length)) return
      this._demo = true
      // Anchor timestamps to demo-today (NOT the real calendar day) so notifications sit
      // on the same timeline as the rest of the demo — but keep the real time-of-day so
      // they still feel freshly generated ("this morning", "20 min ago").
      const real = new Date()
      const now = demoDaysAgo(0)
      now.setUTCHours(real.getUTCHours(), real.getUTCMinutes(), 0, 0)
      const lastEtl = new Date(now)
      lastEtl.setUTCHours(4, 10, 0, 0)
      if (lastEtl > now) lastEtl.setUTCDate(lastEtl.getUTCDate() - 1)   // early morning → yesterday's run
      // The holiday outlook is Home + mail only, never the bell (as in production).
      this.allen = DEMO_ALLEN_NOTIFICATIONS
        .filter((n) => n.kind !== 'calendar_outlook')
        .map((n) => ({ ...n, createdAt: lastEtl.toISOString() }))
      try {
        const j = await loadDemoJSON('/demo_data/demo_notifications.json')
        const live = Array.isArray(j) ? j : []
        this.live = live.map((n, i) => ({
          ...n,
          TimeStamp: new Date(now.getTime() - (20 + i * 35) * 60000).toISOString(),
        }))
      } catch (e) { this.live = [] }
    },

    // ---------- PROD ----------
    async fetchAllen() {
      try {
        const { data } = await axios.get(`${API_URL}/coach/notifications`)
        this.allen = ((data && data.notifications) || []).map(mapCoachNotification)
      } catch (e) { this.allen = [] }
    },

    // ---------- shared UI ----------
    openTo(tab) { this.activeTab = tab || 'live'; this.open = true },
    setTab(tab) { this.activeTab = tab },
    close() { this.open = false },

    // ---------- live (demo only) ----------
    acknowledgeLive(id) { this.live = this.live.filter((n) => n.NotificationId !== id) },
    clearLive(ids) { const s = new Set(ids); this.live = this.live.filter((n) => !s.has(n.NotificationId)) },

    // ---------- allen ----------
    /**
     * View = go to it; leaves the inbox (prod: mark dismissed).
     *
     * EXCEPT a "was that a promo?" question, where dismissed carries a second
     * meaning the producer relies on: it is the record that says "not a promo, never
     * ask again". Viewing is how you ANSWER that question — it opens the log form —
     * so dismissing on view destroyed the question before it could be answered, and
     * the scanner never raises a signature twice. Click through, change your mind,
     * and the window was never asked about again. It stays `read` and in the inbox
     * until explicitly cleared, which is the only act that means "it wasn't one".
     */
    viewAllen(id, kind = null) {
      const keep = kind === 'promo_candidate'
      if (!keep) this.allen = this.allen.filter((n) => n.id !== id)
      if (!this._demo) {
        axios.patch(`${API_URL}/coach/notifications/${id}/status`,
                    { status: keep ? 'read' : 'dismissed' }).catch(() => {})
      }
    },
    clearAllen(ids) {
      const s = new Set(ids)
      this.allen = this.allen.filter((n) => !s.has(n.id))
      if (!this._demo) {
        ids.forEach((id) => axios.patch(`${API_URL}/coach/notifications/${id}/status`, { status: 'dismissed' }).catch(() => {}))
      }
    },
  },
})
